import { Helmet } from "react-helmet-async";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  { id: "scope", title: "Scope" },
  { id: "prohibited", title: "Prohibited Conduct" },
  { id: "ip", title: "IP & Idea Integrity" },
  { id: "ai-misuse", title: "AI Misuse" },
  { id: "security", title: "Security & Integrity" },
  { id: "dmca", title: "Copyright (DMCA)" },
  { id: "idea-theft", title: "Idea-Theft Complaints" },
  { id: "enforcement", title: "Enforcement" },
  { id: "reporting", title: "Reporting Abuse" },
];

const AcceptableUse = () => (
  <>
    <Helmet>
      <title>Acceptable Use Policy — The Emerging Market</title>
      <meta name="description" content="What you can and cannot do on The Emerging Market, including our copyright and idea-theft complaint processes." />
      <link rel="canonical" href="https://emrkt.co/acceptable-use" />
    </Helmet>
    <LegalLayout
      title="Acceptable Use Policy"
      description="Rules for using The Emerging Market — designed to protect users' ideas, intellectual property, and the integrity of the platform."
      lastUpdated="June 5, 2026"
      effectiveDate="June 5, 2026"
      sections={sections}
    >
      <section id="scope">
        <h2>1. Scope</h2>
        <p>
          This Acceptable Use Policy ("AUP") applies to every user of The Emerging Market and is incorporated
          into our <a href="/terms">Terms of Service</a>. Violating this AUP can result in content removal,
          account suspension, or termination.
        </p>
      </section>

      <section id="prohibited">
        <h2>2. Prohibited Conduct</h2>
        <p>You agree not to use the Service to:</p>
        <ul>
          <li>Break any law or regulation, or facilitate illegal activity;</li>
          <li>Harass, threaten, defame, or discriminate against any person or group;</li>
          <li>Post hateful, violent, sexually explicit, or otherwise objectionable content;</li>
          <li>Dox or publish another person's private or identifying information without consent;</li>
          <li>Spam, send unsolicited commercial messages, or mass-create accounts;</li>
          <li>Impersonate another person, organization, or affiliation;</li>
          <li>Mislead funders, partners, or the public about the status, scope, or sponsorship of an idea or project;</li>
          <li>Submit knowingly false information in applications, asks, or affiliate listings;</li>
          <li>Upload malware, viruses, or other harmful code.</li>
        </ul>
      </section>

      <section id="ip">
        <h2>3. Intellectual Property & Idea Integrity</h2>
        <p>Because the Service is built around community ideas, we take IP integrity especially seriously. You must not:</p>
        <ul>
          <li>Post text, images, audio, video, datasets, or code you do not own or have permission to share;</li>
          <li>Submit a third party's trade secrets, confidential information, or proprietary materials (including those of a current or former employer);</li>
          <li>Claim another user's idea, project, or work as your own;</li>
          <li>Re-publish another user's private or members-only content without their consent;</li>
          <li>Use TEM's brand, logos, or marks without our written permission;</li>
          <li>Misrepresent endorsements, partnerships, certifications, or grant awards.</li>
        </ul>
        <p>
          If you draw on someone else's work (research, datasets, articles), credit the source. If you adapt
          someone else's idea posted on the Service, attribute them and link back where reasonable.
        </p>
      </section>

      <section id="ai-misuse">
        <h2>4. AI Misuse</h2>
        <p>You must not:</p>
        <ul>
          <li>Use AI features to generate disinformation, deepfakes, or impersonate real people;</li>
          <li>Attempt to extract system prompts, model weights, or other users' inputs;</li>
          <li>Submit content designed to bypass safety or rate-limiting controls;</li>
          <li>Use AI outputs to commit fraud (e.g., fabricated grant data, forged credentials).</li>
        </ul>
      </section>

      <section id="security">
        <h2>5. Security & Platform Integrity</h2>
        <p>You must not:</p>
        <ul>
          <li>Probe, scan, or test the vulnerability of the Service without our prior written consent;</li>
          <li>Bypass or attempt to bypass authentication, authorization, rate limits, or access controls;</li>
          <li>Scrape, crawl, or harvest data except through interfaces we expressly provide;</li>
          <li>Interfere with or disrupt the Service, its servers, or networks;</li>
          <li>Use the Service to train AI models without our written permission;</li>
          <li>Reverse-engineer, decompile, or extract source code from the Service.</li>
        </ul>
        <p>
          Responsible security research is welcome — please report findings privately to{" "}
          <a href="mailto:info@emrkt.co">info@emrkt.co</a> before public disclosure.
        </p>
      </section>

      <section id="dmca">
        <h2>6. Copyright Complaints (DMCA)</h2>
        <p>
          We respect copyright. If you believe content on the Service infringes your copyright, send a written
          notice to our designated agent containing:
        </p>
        <ol>
          <li>Your physical or electronic signature;</li>
          <li>Identification of the copyrighted work claimed to have been infringed;</li>
          <li>Identification of the allegedly infringing material and information sufficient for us to locate it (URL);</li>
          <li>Your name, address, telephone number, and email;</li>
          <li>A statement that you have a good-faith belief that the use is not authorized by the copyright owner, its agent, or the law;</li>
          <li>A statement, under penalty of perjury, that the information is accurate and that you are the owner or authorized to act on the owner's behalf.</li>
        </ol>
        <p>
          <strong>Designated Agent:</strong> The Emerging Market — DMCA Agent, <a href="mailto:info@emrkt.co">info@emrkt.co</a>, Mobile, Alabama, USA.
        </p>
        <p>
          <strong>Counter-notice:</strong> if you believe content of yours was removed in error, you may send a
          counter-notice with the elements required by 17 U.S.C. § 512(g) to the same address.
        </p>
        <p>
          <strong>Repeat infringers:</strong> we will terminate accounts of users who repeatedly infringe
          copyright, in appropriate circumstances.
        </p>
      </section>

      <section id="idea-theft">
        <h2>7. Idea-Theft & Misappropriation Complaints</h2>
        <p>
          If you believe another user has copied your idea, project, dataset, or trade secret in a way that
          isn't covered by copyright (e.g., misappropriation of a confidential concept, plagiarism of a project
          plan), email <a href="mailto:info@emrkt.co">info@emrkt.co</a> with the subject line
          "Idea-Theft Report" and include:
        </p>
        <ul>
          <li>Links to both the original and the allegedly copied content;</li>
          <li>Dates and evidence demonstrating your prior authorship or development;</li>
          <li>A description of what was copied and why you believe it was not independently developed;</li>
          <li>Your contact information.</li>
        </ul>
        <p>
          We will review reports in good faith. We are not a court and cannot adjudicate ownership disputes,
          but we will remove content that clearly violates this AUP and may suspend repeat offenders. For
          formal remedies, consult an attorney.
        </p>
      </section>

      <section id="enforcement">
        <h2>8. Enforcement</h2>
        <p>Depending on severity and history, we may:</p>
        <ul>
          <li>Issue a warning;</li>
          <li>Hide, edit, or remove content;</li>
          <li>Limit account features;</li>
          <li>Suspend or terminate the account;</li>
          <li>Report illegal activity to law enforcement.</li>
        </ul>
        <p>We may take action without prior notice when necessary to protect users, the Service, or third parties.</p>
      </section>

      <section id="reporting">
        <h2>9. Reporting Abuse</h2>
        <p>
          To report a violation of this AUP, email <a href="mailto:info@emrkt.co">info@emrkt.co</a>. Provide
          enough detail (URLs, screenshots, context) for us to investigate. We will not share your identity
          with the reported user unless required by law.
        </p>
      </section>
    </LegalLayout>
  </>
);

export default AcceptableUse;